/* TODO: Fix me */
/* eslint max-lines: 0 */
import { Directive, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ActionType, Actions, Store, ofActionCompleted, ofActionDispatched, ofActionSuccessful } from '@ngxs/store';
import { ToastContainerDirective } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { ofActionErroredDetail } from '../error-handling';
import { NotificationsService } from '../services/notifications-service/notifications.service';
import { TranslationService } from '../transloco/services/translation.service';
import { ComponentBaseService } from './component.base.service';
import { ILocalize } from '../interfaces/localize.interface';

export interface IOnRouteParamsChanged {
    routeParamsChanged(params: Params): void;
}

export interface IOnRouteQueryParamsChanged {
    routeQueryParamsChanged(params: Params): void;
}

export interface IAllowRealTimeUpdates {
    startRealTimeUpdates(): void;
    stopRealTimeUpdates(): void;
}

@Directive()
export abstract class ComponentBaseDirective implements OnDestroy {
    private loaderIds: string[] = [];

    protected subscription = new Subscription();
    protected loaderService: NgxUiLoaderService;
    protected actions$: Actions;
    protected notificationsService: NotificationsService;
    protected translationService: TranslationService;

    public store: Store;

    constructor() {
        const componentBaseService = inject(ComponentBaseService);

        this.loaderService = componentBaseService.loaderService;
        this.actions$ = componentBaseService.actions$;
        this.store = componentBaseService.store;
        this.notificationsService = componentBaseService.notificationsService;
        this.translationService = componentBaseService.translationService;
    }

    public ngOnDestroy() {
        this.notificationsService.notificationContainer = null;
        this.unregisterLoaders();

        this.subscription.unsubscribe();
    }

    protected registerNotificationContainer(container: ToastContainerDirective) {
        this.notificationsService.notificationContainer = container;
    }

    protected addRouteParamsChanged(route: ActivatedRoute, onlyOnce = true) {
        const routeParams = route.params;
        if (onlyOnce) {
            routeParams.pipe(take(1));
        }
        this.subscribe(routeParams, (params) => {
            const self = this as unknown as IOnRouteParamsChanged;
            if (self.routeParamsChanged != null) {
                self.routeParamsChanged(params);
            }
        });
    }

    protected addRouteQueryParamsChanged(route: ActivatedRoute, onlyOnce = true) {
        const routeParams = route.queryParams;
        if (onlyOnce) {
            routeParams.pipe(take(1));
        }
        this.subscribe(routeParams, (params) => {
            const self = this as unknown as IOnRouteQueryParamsChanged;
            if (self.routeQueryParamsChanged != null) {
                self.routeQueryParamsChanged(params);
            }
        });
    }

    protected addLoader(loaderName: string, ...allowedTypes: ActionType[]) {
        this.loaderIds.push(loaderName);
        const dispatchedActions = [] as Array<object>;
        this.ofActionDispatchedInternal(() => this.loaderService.startLoader(loaderName), allowedTypes, dispatchedActions);
        this.ofActionCompletedInternal(() => this.loaderService.stopLoader(loaderName), allowedTypes, dispatchedActions);
    }

    protected addBackgroundLoader(loaderName: string, ...allowedTypes: ActionType[]) {
        this.ofActionDispatched(() => this.loaderService.startBackgroundLoader(loaderName), ...allowedTypes);
        this.ofActionCompleted(() => this.loaderService.stopBackgroundLoader(loaderName), ...allowedTypes);
    }

    protected addBreadcrumb(callback: () => void, ...allowedTypes: ActionType[]) {
        this.ofActionCompleted(() => callback(), ...allowedTypes);
    }

    protected addSuccessNotification(message: string, ...allowedTypes: ActionType[]): void;
    protected addSuccessNotification(message: string, title: string, ...allowedTypes: ActionType[]): void;
    protected addSuccessNotification(message: string, title: string | ActionType, ...allowedTypes: ActionType[]): void {
        if (typeof title === 'string') {
            this.ofActionSuccessful(() => this.notificationsService.success(message, title), ...allowedTypes);
        } else {
            this.ofActionSuccessful(() => this.notificationsService.success(message), title, ...allowedTypes);
        }
    }

    protected addSuccessNotificationCallback(text: string, callback: () => void, ...allowedTypes: ActionType[]) {
        this.ofActionSuccessful(
            () => {
                this.notificationsService.success(text);
                callback();
            },
            ...allowedTypes
        );
    }

    protected addSuccessNotificationWithLanguageKey(localize: ILocalize, ...allowedTypes: ActionType[]): void;
    protected addSuccessNotificationWithLanguageKey(localize: ILocalize, title: string, ...allowedTypes: ActionType[]): void;
    protected addSuccessNotificationWithLanguageKey(localize: ILocalize, title: string | ActionType, ...allowedTypes: ActionType[]): void {
        if (typeof title === 'string') {
            this.ofActionSuccessful(() => this.notificationsService.success(this.translationService.translate(localize.key, localize.parameters), title), ...allowedTypes);
        } else {
            this.ofActionSuccessful(() => this.notificationsService.success(this.translationService.translate(localize.key, localize.parameters)), title, ...allowedTypes);
        }
    }

    protected addSuccessNotificationCallbackWithLanguageKey(localize: ILocalize, callback: () => void, ...allowedTypes: ActionType[]) {
        this.ofActionSuccessful(
            () => {
                this.notificationsService.success(this.translationService.translate(localize.key, localize.parameters));

                callback();
            },
            ...allowedTypes
        );
    }

    protected addErrorNotificationWithLanguageKey(localize: ILocalize, ...allowedTypes: ActionType[]) {
        this.ofActionErrored((result) => this.notificationsService.error(this.translationService.translate(localize.key, localize.parameters), result.error), ...allowedTypes);
    }

    protected addErrorNotification(text: string, ...allowedTypes: ActionType[]) {
        this.ofActionErrored((result) => this.notificationsService.error(text, result.error), ...allowedTypes);
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    protected ofActionSuccessful(callback: (state?: any) => void, ...allowedTypes: ActionType[]) {
        this.subscribe(this.actions$.pipe(ofActionSuccessful(...allowedTypes)), (state) => callback(state));
    }

    protected ofActionCompleted(callback: () => void, ...allowedTypes: ActionType[]) {
        this.ofActionCompletedInternal(callback, allowedTypes);
    }

    private ofActionCompletedInternal(callback: () => void, allowedTypes: ActionType[], trackedActions: object[] = undefined) {
        this.subscribe(this.actions$.pipe(ofActionCompleted(...allowedTypes)), (a) => {
            if (trackedActions != null) {
                const idx = trackedActions.indexOf(a.action);
                const isTrackedAction = idx >= 0;

                if (!isTrackedAction) {
                    // if action tracking is activated.
                    return;
                }

                // remove completed action from tracking.
                trackedActions.splice(idx, 1);

                return trackedActions.length === 0 ? callback() : of();
            }

            return callback();
        });
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    protected ofActionErrored(callback: (result: any) => void, ...allowedTypes: ActionType[]) {
        this.subscribe(this.actions$.pipe(ofActionErroredDetail(...allowedTypes)), (result) => callback(result));
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    protected ofActionDispatched(callback: (actionContext: any) => void, ...allowedTypes: ActionType[]) {
        this.ofActionDispatchedInternal(callback, allowedTypes);
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    private ofActionDispatchedInternal(callback: (actionContext: any) => void, allowedTypes: ActionType[], trackedActions: object[] = undefined) {
        this.subscribe(this.actions$.pipe(ofActionDispatched(...allowedTypes)), (a) => {
            trackedActions && trackedActions.push(a);
            callback(a);
        });
    }

    protected subscribe<T>(observable: Observable<T>, callback: (result: T) => void) {
        if (observable == null) {
            return;
        }

        this.subscription.add(observable.subscribe(callback));
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    protected select<T>(selector: (state: any, ...states: any[]) => T, updateFunc: (newValue: T) => void) {
        this.subscription.add(this.store.select(selector).subscribe((newValue) => updateFunc(newValue)));
    }

    protected addLoadingObservable(...allowedTypes: ActionType[]) {
        const subject = new BehaviorSubject(false);

        this.ofActionDispatched(() => subject.next(true), ...allowedTypes);
        this.ofActionCompleted(() => subject.next(false), ...allowedTypes);

        return subject.asObservable();
    }

    private unregisterLoaders() {
        for (const loaderId of this.loaderIds) {
            const loader = this.loaderService.getLoader(loaderId);
            if (loader) {
                this.loaderService.destroyLoaderData(loaderId);
            }
        }
    }

    private capitalize(name: string) {
        return name.charAt(0).toUpperCase() + name.slice(1);
    }
}
